// Typed client for the lazior-api console API.
//
// This console is hosted on its own origin, so it authenticates with a bearer
// session token (the API's cookie flow is same-origin only). The token is
// issued by POST /console/google-login to clients that identify themselves with
// X-Console-Client: external, and is held in localStorage so a reload resumes
// the session.

import type {
  AIUsage,
  CapiScore,
  Workflow,
  AftersaleStep,
  AIReadRow,
  FollowupContent,
  FollowupLabels,
  FollowupRound,
  FollowupSendResult,
  AIHealth,
  AIVersus,
  BoardPage,
  AdPerson,
  AdRemark,
  CostReport,
  GrowthActionState,
  GrowthResponse,
  SegmentKind,
  SegmentProduct,
  SegmentReport,
  SegmentSync,
  OrdersResponse,
  AttachedPhone,
  UnlinkedOrder,
  ActionResult,
  AuditResponse,
  CampaignsResponse,
  AdsResponse,
  OriginReportRow,
  UnplacedSales,
  LiveReport,
  LeadDetail,
  LeadFilters,
  LeadFacets,
  LeadPulse,
  ShopOrder,
  LeadsResponse,
  Me,
  Overview,
  ReengageStats,
  SettingsResponse,
  SpendRefreshResult,
  RolesResponse,
  UsersResponse,
  PlaybookOverview,
  PlaybookProduct,
  PlaybookDetail,
  PlaybookStep,
  PreviewTurn,
  PlaybookAsset,
  PlaybookStats,
  AIOverview,
  AIProfile,
  AISnapshot,
  AIAdPrice,
  AIAsset,
  MomentsState,
  PromptMode,
  ReplayState,
  LibraryVideo,
  LibraryVoice,
  AITrial,
  AITrialTurn,
  AIExamCase,
  AIExamExpect,
  AIExamState,
  AIExamQuote,
  AINeverSaid,
  AIReplayChat,
  AIEdit,
  AIToneReport,
  ExamAsk,
  AIAsset as AIAssetRow,
  InsightState,
  PriceSG,
  PriceSheet,
  LeadReason,
  GrowthExperiment,
  ExperimentArm,
  ExperimentMetric,
  WeeklyReview,
  WorkflowTrialRow,
} from "./types";

/** What the page sends for an exam case. */
export interface ExamCaseBody {
  title: string;
  history: AITrialTurn[];
  customer_text: string;
  expect: AIExamExpect;
  enabled?: boolean;
}

/** Set by public/config.js at runtime — never baked into the bundle. */
declare global {
  interface Window {
    LAZIOR_API_BASE?: string;
  }
}

/**
 * The sales module namespaces every route it owns, leaving the platform's root
 * paths to its other modules. config.js holds the plain origin; the prefix is
 * part of the API contract and lives here.
 */
const API_PREFIX = "/sales-api/v1";

export const BASE = String(window.LAZIOR_API_BASE ?? "").replace(/\/+$/, "") + API_PREFIX;
export const TOKEN_KEY = "lazior_console_token";

/**
 * How many conversations one list request pulls. The backend defaults to 50
 * and caps at 1000; asking explicitly keeps the list from being silently
 * truncated at 50 while the CSV export returns everything.
 */
export const LIST_LIMIT = 200;
/** A work list asks for the backend's cap (handler.go maxListLimit): see listLimit. */
export const WORK_LIST_LIMIT = 1000;

/**
 * Mirror of the stored token. Storage can be unavailable (Safari private mode,
 * enterprise policy); without this fallback a successful login would be
 * followed by an immediate 401 and a sign-in loop.
 */
let memToken: string | null = null;

export function getToken(): string | null {
  if (memToken) return memToken;
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function setToken(token: string): void {
  memToken = token;
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* session still works in this tab via memToken; a reload will need a new login */
  }
}

/** The start of what a page keeps in this browser to draw at its next opening (ScriptBoardView's boards): it goes with the session. */
export const KEPT = "kept:";

export function clearToken(): void {
  memToken = null;
  try {
    localStorage.removeItem(TOKEN_KEY);
    for (const k of Object.keys(localStorage)) if (k.startsWith(KEPT)) localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Called whenever the API rejects our session (401). The app registers a
 * handler that drops back to the sign-in screen; the token is already cleared
 * by the time it runs.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/**
 * headers() without the JSON content type. A multipart body needs the browser
 * to write the boundary into Content-Type itself, and a preset value stops it —
 * the server then sees a JSON header on a body that is not JSON and refuses.
 */
function multipartHeaders(): Headers {
  const h = headers();
  h.delete("Content-Type");
  return h;
}

function headers(extra?: HeadersInit): Headers {
  const h = new Headers(extra);
  h.set("Content-Type", "application/json");
  // Opt in to the bearer flow: only then does sign-in return a token.
  h.set("X-Console-Client", "external");
  const tok = getToken();
  if (tok) h.set("Authorization", `Bearer ${tok}`);
  return h;
}

/**
 * A failed fetch cross-origin is indistinguishable from an outage, and by far
 * the most common cause in a fresh deploy is a missing CORS entry — so say so.
 */
function networkError(): ApiError {
  return new ApiError(
    0,
    `Cannot reach the API at ${BASE || "(unset LAZIOR_API_BASE)"}. If the service is up, check that its CORS_ORIGINS includes ${window.location.origin}.`,
  );
}

/** Surfaces the backend's own message ("no console account for …"). */
async function errorFrom(res: Response): Promise<ApiError> {
  let detail = "";
  try {
    detail = (await res.text()).trim().split("\n")[0]?.slice(0, 200) ?? "";
  } catch {
    /* body already consumed or unreadable */
  }
  return new ApiError(res.status, detail || `HTTP ${res.status}`);
}

/**
 * Writes on their way to the server. A tab reloading itself for a new build
 * (fresh.tsx) waits for every one: cut off midway, a reply may or may not
 * have reached the customer.
 */
let writing = 0;
export const writesInFlight = () => writing;
/** Writes started from this tab so far: a page read before the last one may be out of date. */
let written = 0;
export const writesSoFar = () => written;

/** fetch, counting anything but a GET until its answer arrives. Every write goes through here. */
async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const write = (init.method ?? "GET") !== "GET";
  const release = write ? holdPressed() : undefined;
  if (write) {
    writing++;
    written++;
  }
  try {
    return await fetch(url, init);
  } finally {
    if (write) {
      writing--;
      release?.();
      // Whatever was written may be in a kept report: the next look asks again.
      kept.clear();
    }
  }
}

/**
 * The button a press is being handled for, until the browser moves on to its
 * next task: a write started while handling it — in its handler, or in what
 * the handler set off at once — belongs to that button. The button then turns
 * in place and takes no second press until the server has answered, and for
 * at least 0.6 s, so a double click is never a second order, message or
 * hand-back (owner 2026-10-21: 「用户会一直点一直按」). A write started later
 * (a timer, after waiting for something else) holds nothing: the button
 * pressed last may not be the one it came from.
 */
let pressedButton: HTMLElement | null = null;
const HOLD_AT_LEAST_MS = 600;
/** A write that never answers must not leave its button dead for good. */
const HOLD_AT_MOST_MS = 60_000;
const held = new WeakMap<HTMLElement, { writes: number; since: number }>();
if (typeof window !== "undefined") {
  const nextTask = new MessageChannel();
  nextTask.port1.onmessage = () => {
    pressedButton = null;
  };
  window.addEventListener(
    "click",
    (e) => {
      const b = e.target instanceof Element ? e.target.closest<HTMLElement>("button, [role='button'], [role='switch']") : null;
      // Held: the press goes nowhere, keyboard ones included. Caught here, on the
      // way down, before the page's own handlers hear of it.
      if (b?.dataset.busy) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      // A menu item or a tab opens a page; a save that happens to go out as it
      // does (小爱's words, written on leaving) is not its doing.
      pressedButton = b && !b.matches(".tab, .pnav-i, .psheet-row, [role='tab']") ? b : null;
      if (b) nextTask.port2.postMessage(0);
    },
    { capture: true },
  );
}
function holdPressed(): (() => void) | undefined {
  const b = pressedButton;
  if (!b || !b.isConnected) return undefined;
  const h = held.get(b) ?? { writes: 0, since: performance.now() };
  h.writes++;
  held.set(b, h);
  b.dataset.busy = "1";
  // A button that says aria-busy itself keeps saying it.
  const owned = !b.hasAttribute("aria-busy");
  if (owned) b.setAttribute("aria-busy", "true");
  let done = false;
  const release = () => {
    if (done) return;
    done = true;
    window.clearTimeout(cap);
    window.setTimeout(
      () => {
        if (--h.writes > 0) return;
        held.delete(b);
        delete b.dataset.busy;
        if (owned) b.removeAttribute("aria-busy");
      },
      Math.max(0, HOLD_AT_LEAST_MS - (performance.now() - h.since)),
    );
  };
  const cap = window.setTimeout(release, HOLD_AT_MOST_MS);
  return release;
}

/**
 * Reads somebody is waiting for, for the line along the top of the page
 * (LoadBar.tsx). Owner 2026-10-16: 「加载中是不是要有ui显示出来不然看起来像卡了 用户就会继续一直按」;
 * 2026-10-21: 「画面加载只在最上面？…用户会一直点一直按，画面加载得更慢」; 2026-10-27:
 * 「总是看见上面loading的动态在跑看起来画面像还在加载然后要等很久」.
 *
 * What somebody waits for is a page with nothing on it yet — its placeholder,
 * which counts as one wait for as long as it is up (waitForPage) — and what a
 * press inside the page asked for: a chat, a date, the next rows. A read counts
 * when it starts within two seconds of a click or a key, for the page on
 * screen then. Never: a read a timer or coming back to the tab started
 * (quietly, hooks.ts) — those ran the line whenever a half-minute refresh fell
 * within two seconds of any click — nor the reads a menu item's page starts on
 * being shown, which on a page already drawn are its refresh in the background:
 * back on 今天, its eight lists ran the line 0.4–1.4 s over a page already there.
 * Leaving a page leaves its read behind: it still lands there, but the line is
 * about the page in front of you only.
 */
let lastInput = Number.NEGATIVE_INFINITY;
/** The page whose menu item was the last thing pressed: until the next press, what it reads is its own refresh. */
let menuFor = "";
let onScreen = "";
/** Inside a timer's or a return's callback (quietly). */
let quietDepth = 0;
if (typeof window !== "undefined") {
  const mark = () => {
    lastInput = performance.now();
    menuFor = "";
  };
  window.addEventListener("pointerdown", mark, { capture: true, passive: true });
  window.addEventListener("keydown", mark, { capture: true, passive: true });
}
/**
 * Runs fn as nobody's wait: every read it starts is a refresh, whenever the last click was. For timers and returns
 * (hooks.ts useInterval, useOnReturn, useOnVisible); the reads must start before fn's first await, as they do there.
 */
export function quietly<T>(fn: () => T): T {
  quietDepth++;
  try {
    return fn();
  } finally {
    quietDepth--;
  }
}
/** For one page: reads somebody is waiting for. */
export interface Waits {
  reads: number;
}
const NO_WAITS: Waits = { reads: 0 };
const waits = new Map<string, Waits>();
const watchers = new Set<(w: Waits) => void>();
const waitsShown = () => waits.get(onScreen) ?? NO_WAITS;
function tell() {
  const w = waitsShown();
  for (const fn of watchers) fn(w);
}
/** Called with the page on screen's waits, now and on every change; returns the way to stop. */
export function watchWaiting(fn: (w: Waits) => void): () => void {
  watchers.add(fn);
  fn(waitsShown());
  return () => {
    watchers.delete(fn);
  };
}
/** The page now on screen, by its menu key (App). */
export function setPageOnScreen(page: string): void {
  if (page === onScreen) return;
  onScreen = page;
  tell();
}
/** A menu item was pressed: what its page reads on being shown is not waited for (its placeholder is, if it has one up). */
export function pressedMenu(page: string): void {
  menuFor = page;
  lastInput = performance.now();
  setPageOnScreen(page);
}
/** A read starting: counted for the page on screen if somebody asked for it. Returns its end. */
function waitFor(quiet: boolean): () => void {
  if (quiet || quietDepth > 0 || performance.now() - lastInput >= 2000 || (menuFor !== "" && menuFor === onScreen)) return () => undefined;
  return waitOn(onScreen);
}
/**
 * A page's placeholder up (PageSkel): the page is waited for until it goes.
 * A first read can be a chain — 剧本 reads its products, then the product's,
 * then the working and the live copy, one after another — and on a slow line
 * the later reads start more than two seconds after the press, so on their
 * own they lit nothing: with every read held 2 s the line went out at 2.4 s
 * while the placeholder stood until 6.5 s (QA 2026-10-22). Returns its end.
 */
export function waitForPage(page: string): () => void {
  return waitOn(page);
}
function waitOn(page: string): () => void {
  const was = waits.get(page) ?? NO_WAITS;
  waits.set(page, { reads: was.reads + 1 });
  if (page === onScreen) tell();
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    const now = waits.get(page) ?? NO_WAITS;
    waits.set(page, { reads: Math.max(0, now.reads - 1) });
    if (page === onScreen) tell();
  };
}

/**
 * Reads on their way, by path: two parts of a page asking for the same thing
 * at once share one request. 经营 keeps its 广告 and 订单 pages mounted, and a
 * new date sent the same four report requests from each — eight at once, the
 * slowest 3.5 s.
 */
const reading = new Map<string, Promise<unknown>>();

/**
 * Reports kept for half a minute, so going back to a page, a tab or a date just
 * seen shows it at once instead of working it out again. Only reports nobody
 * edits here and nothing polls for progress; any write from this tab clears
 * them all.
 */
const KEEP_MS = 30_000;
const KEEPABLE = [
  /^\/console\/api\/board\?/,
  /^\/console\/api\/report\/(overview|campaigns|ads|origins|costs|segments|capi)\b/,
  /^\/console\/api\/growth\/reviews\b/,
  /^\/console\/api\/ai\/versus\?/,
  /^\/console\/api\/ai-tone\b/,
  /^\/console\/api\/library\/colleague-(videos|voices)\b/,
];
const kept = new Map<string, { at: number; value: unknown }>();

/** Every caller gets its own copy, so one page changing what it was given cannot change another's. */
const copy = <T,>(v: T): T =>
  v === null || typeof v !== "object" ? v : typeof structuredClone === "function" ? structuredClone(v) : (JSON.parse(JSON.stringify(v)) as T);

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(BASE + path, {
      ...init,
      credentials: "omit", // cross-origin: the bearer token is the only credential
      headers: headers(init.headers),
    });
  } catch {
    throw networkError();
  }
  if (res.status === 401) {
    clearToken();
    onUnauthorized?.();
    throw new ApiError(401, "unauthorized");
  }
  if (res.status === 403) throw new ApiError(403, "You don't have access to that.");
  if (!res.ok) throw await errorFrom(res);
  if (res.status === 204) return null as T;
  return (await res.json()) as T;
}

/** 跟进's round (api.followupBoard), for watchRead. */
export const FOLLOWUP_BOARD = "/console/api/followup/board";

/** Who hears every answer to a path, whichever page asked: a number shown away from that page (App's 跟进). */
const heard = new Map<string, Set<(v: unknown) => void>>();
/** Calls fn with each answer to `path` that lands from now on; returns the way to stop. Read it, never change it. */
export function watchRead<T>(path: string, fn: (v: T) => void): () => void {
  const set = heard.get(path) ?? new Set<(v: unknown) => void>();
  const f = fn as (v: unknown) => void;
  set.add(f);
  heard.set(path, set);
  return () => {
    set.delete(f);
  };
}

function get<T>(path: string, quiet = false): Promise<T> {
  const keepable = KEEPABLE.some((re) => re.test(path));
  if (keepable) {
    const k = kept.get(path);
    if (k && Date.now() - k.at < KEEP_MS) return Promise.resolve(copy(k.value as T));
  }
  // Shared, the read is waited for here too: a page that joins another
  // page's read turns its own item and line until it lands.
  const ended = waitFor(quiet);
  const on = reading.get(path);
  if (on) return on.then((v) => copy(v as T)).finally(ended);
  const p = request<T>(path)
    .then((v) => {
      if (keepable) kept.set(path, { at: Date.now(), value: v });
      heard.get(path)?.forEach((f) => {
        try {
          f(v);
        } catch {
          /* a listener never breaks the read */
        }
      });
      return v;
    })
    .finally(() => {
      reading.delete(path);
      ended();
    });
  reading.set(path, p);
  return p.then(copy);
}

/** A file the API serves behind the bearer token, as a blob. */
/**
 * Whether this device plays WhatsApp's voice notes, which are Ogg/Opus — the
 * customers', a colleague's from the phone, 小爱's. Safari before iOS 18.4
 * plays no Ogg at all (owner, 2026-10-20: 「ios用户用sales.lazior.com听不见对话里
 * 的语音」). Asked once.
 */
let oggPlays: boolean | undefined;
export function playsOgg(): boolean {
  if (oggPlays === undefined) {
    try {
      const a = document.createElement("audio");
      oggPlays = a.canPlayType("audio/ogg; codecs=opus") !== "" || a.canPlayType("audio/ogg") !== "";
    } catch {
      oggPlays = true;
    }
  }
  return oggPlays;
}

/** A recording this device would need another copy of: Ogg or Opus. */
const oggish = (s: string) => /\b(ogg|opus)\b/i.test(s);

/**
 * A stored file as a blob, by its route — the route a picture, a clip or a
 * voice note is fetched by (media, aiAsset). Not apiFetch: a file is not a
 * page's wait for the top bar.
 */
async function fileAt(path: string, signal?: AbortSignal): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(BASE + path, { credentials: "omit", headers: headers(), signal });
  } catch {
    throw networkError();
  }
  if (res.status === 401) {
    clearToken();
    onUnauthorized?.();
    throw new ApiError(401, "unauthorized");
  }
  if (res.status === 403) throw new ApiError(403, "You don't have access to that.");
  if (!res.ok) throw await errorFrom(res);
  return res.blob();
}

/**
 * The file at path, as this device can play it: on a phone that plays no Ogg,
 * a voice note comes as the API's M4A copy (?as=m4a, lazior-api playable.go)
 * — asked for first when the key already says it is an Ogg, else when the
 * file turns out to be one. A copy that cannot be had leaves the file itself,
 * which the player then says it cannot play, with the file to download.
 */
async function playableAt(path: string, key: string, signal?: AbortSignal): Promise<Blob> {
  const m4a = () => fileAt(path + (path.includes("?") ? "&" : "?") + "as=m4a", signal).catch(() => null);
  if (!playsOgg() && /\.(ogg|opus)$/i.test(key)) {
    const copy = await m4a();
    if (copy) return copy;
  }
  const b = await fileAt(path, signal);
  if (!playsOgg() && oggish(b.type)) return (await m4a()) ?? b;
  return b;
}

async function blobAt(path: string): Promise<Blob> {
  let res: Response;
  try {
    res = await apiFetch(BASE + path, { credentials: "omit", headers: headers() });
  } catch {
    throw networkError();
  }
  if (res.status === 401) {
    clearToken();
    onUnauthorized?.();
    throw new ApiError(401, "unauthorized");
  }
  if (!res.ok) throw await errorFrom(res);
  return res.blob();
}

/**
 * A GET for a list the console polls, sending back the tag of the copy it
 * already holds. Unchanged, the API answers 304 with no body and this returns
 * a null body under the same tag (lazior-api list_tag.go, 2026-10-01). Never
 * through the browser's cache: the customer list stays in this tab's memory.
 * An API that sends no tag gets no tag back, and answers in full as before.
 */
async function getIfChanged<T>(path: string, tag: string, quiet = false): Promise<{ body: T | null; tag: string }> {
  // Waited for like any read: 客户表's two seconds used to light nothing at all.
  // Quiet when a page polls it (今天's 发货): never what a press waits for.
  const ended = waitFor(quiet);
  try {
    let res: Response;
    try {
      res = await apiFetch(BASE + path, {
        cache: "no-store",
        credentials: "omit",
        headers: headers(tag ? { "If-None-Match": tag } : undefined),
      });
    } catch {
      throw networkError();
    }
    if (res.status === 304) return { body: null, tag };
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (res.status === 403) throw new ApiError(403, "You don't have access to that.");
    if (!res.ok) throw await errorFrom(res);
    return { body: (await res.json()) as T, tag: res.headers.get("ETag") ?? "" };
  } finally {
    ended();
  }
}

const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) });
const put = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: "PUT", body: JSON.stringify(body ?? {}) });
const del = <T,>(path: string) => request<T>(path, { method: "DELETE" });

const lead = (waID: string) => `/console/api/leads/${encodeURIComponent(waID)}`;

/** An exam press's query: the 红线, the whole exam, what did not pass or changed, or one step of the 工作流. */
function examQuery(ask: ExamAsk, confirm?: number): string {
  const p = new URLSearchParams();
  if (ask.redline) p.set("redline", "1");
  else if (ask.product && ask.step) {
    p.set("product", ask.product);
    p.set("step", ask.step);
  } else if (ask.retake) p.set("only", "failed");
  if (confirm !== undefined) p.set("confirm", String(confirm));
  const q = p.toString();
  return q ? `?${q}` : "";
}

/** No filter at all: every chat. 对话 starts from it; 今天 sets one work list on it. */
export const NO_FILTERS: LeadFilters = {
  q: "",
  ad_id: "",
  product: "",
  network: "",
  status: "",
  lead_stage: "",
  disqualify_hint: false,
  stage: "",
  source: "",
  origin: "",
  label: "",
  bot_off: false,
  bot_on: false,
  held: false,
  needs_human: false,
  purchased: false,
  paid_hint: false,
  followup_today: false,
  followup_missed: false,
  unanswered: false,
  reply: false,
  close: false,
  chase: false,
  aftersale_due: false,
  cod: false,
};

/** Whether the filter is one of the day's work lists, counted as people. */
export const isWorkList = (f: LeadFilters) =>
  f.needs_human || f.reply || f.close || f.chase || f.followup_missed || f.followup_today || f.aftersale_due || f.paid_hint || !!f.cod || !!f.held;

/**
 * How many rows a list asks for. A work list's button counts everybody on it,
 * so its list asks for all of them: at 200, 追一次 stood at 190 (2026-10-22),
 * and past 200 the button would have named people the list never showed.
 * The other lists stay at 200.
 */
export const listLimit = (f: LeadFilters) => (isWorkList(f) ? WORK_LIST_LIMIT : LIST_LIMIT);

/**
 * Shared filter query for the lead list and its CSV export, so "what you see
 * is what you export".
 */
export function leadParams(f: LeadFilters, limit?: number): string {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.ad_id) p.set("ad_id", f.ad_id);
  if (f.product) p.set("product", f.product);
  if (f.network) p.set("network", f.network);
  if (f.status) p.set("status", f.status);
  if (f.stage) p.set("stage", f.stage);
  if (f.source) p.set("source", f.source);
  if (f.origin) p.set("origin", f.origin);
  if (f.label) p.set("label", f.label);
  if (f.bot_off) p.set("bot_off", "1");
  if (f.bot_on) p.set("bot_on", "1");
  if (f.held) p.set("held", "1");
  if (f.needs_human) p.set("needs_human", "1");
  if (f.purchased) p.set("purchased", "1");
  if (f.paid_hint) p.set("paid_hint", "1");
  if (f.lead_stage) p.set("lead_stage", f.lead_stage);
  if (f.disqualify_hint) p.set("disqualify_hint", "1");
  if (f.followup_today) p.set("followup_today", "1");
  if (f.followup_missed) p.set("followup_missed", "1");
  if (f.unanswered) p.set("unanswered", "1");
  if (f.reply) p.set("reply", "1");
  if (f.close) p.set("close", "1");
  if (f.chase) p.set("chase", "1");
  if (f.aftersale_due) p.set("aftersale_due", "1");
  if (f.cod) p.set("cod", "1");
  if (limit) p.set("limit", String(limit));
  return p.toString();
}

export const api = {
  // --- auth ---------------------------------------------------------------
  /**
   * Exchange a Google ID token for a console session. The ID token proves who
   * signed in; whether they may be here is decided server-side by whether an
   * account exists for that address.
   */
  async googleLogin(idToken: string): Promise<Me> {
    const me = await request<Me>("/console/google-login", {
      method: "POST",
      body: JSON.stringify({ id_token: idToken }),
    });
    if (me.token) setToken(me.token);
    return me;
  },

  async logout(): Promise<void> {
    // Clear locally FIRST so a failed request can never leave a usable token
    // behind on a shared machine; the request below still carries it so the
    // server can revoke the session.
    const tok = getToken();
    clearToken();
    if (!tok) return;
    try {
      await apiFetch(BASE + "/console/logout", {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          "X-Console-Client": "external",
          Authorization: `Bearer ${tok}`,
        },
      });
    } catch {
      /* best effort: the local token is already gone */
    }
  },

  me: () => get<Me>("/console/api/me"),

  // --- leads --------------------------------------------------------------
  /** 对话: every open chat, including a number merged into somebody else's —
   *  she still writes from it and the bot still answers. A work list (回复客人,
   *  补跟进, 今天跟进, 售后回访, 说付了) is people, as the customer table counts
   *  them, so there the merged number is left out: the button's number and
   *  the rows under it are the same list. */
  leads: (f: LeadFilters, limit?: number, quiet = false) =>
    get<LeadsResponse>(`/console/api/leads?${isWorkList(f) ? "" : "merged=1&"}${leadParams(f, limit ?? listLimit(f))}`, quiet),
  /** One page of every conversation, for the customer table; a null body when
   *  the copy tagged `tag` is still the answer. */
  customers: (offset: number, limit: number, tag = "") =>
    getIfChanged<LeadsResponse>(`/console/api/leads?limit=${limit}&offset=${offset}`, tag),
  setNote: (waID: string, note: string) => post<{ note: string }>(`${lead(waID)}/note`, { note }),
  /** The cash-on-delivery order is placed in cp: it leaves 货到付款待开单. */
  codDone: (waID: string) => post<{ closed: boolean }>(`${lead(waID)}/cod/done`),
  /** The campaign CS saw on the phone for a customer Meta sent no ad for; "" takes it off. */
  noteCampaign: (waID: string, campaign: string) => post<{ campaign: string }>(`${lead(waID)}/campaign`, { campaign }),
  /** A few conversations matching what was typed, for the 同一个人 picker.
   *  Without merged=1, so a chat already merged is never offered. */
  searchLeads: (q: string, limit = 8) => get<LeadsResponse>(`/console/api/leads?limit=${limit}&q=${encodeURIComponent(q)}`),
  /** 来源: where this customer came from, as a colleague answers it. ""
   *  goes back to what the click says. */
  setOrigin: (waID: string, origin: string) => post<{ origin: string }>(`${lead(waID)}/origin`, { origin }),
  /** 同一个人: this chat is the customer of `into`, so the lists count one of
   *  them. "" takes it back. Nothing moves — both chats stay open. */
  mergeLead: (waID: string, into: string) => post<{ merged_into: string }>(`${lead(waID)}/merge`, { into }),
  /** 不是付款: the customer's 「付了」 was not a payment to us. */
  dismissPaidHint: (waID: string) => post<ActionResult>(`${lead(waID)}/paid-hint/dismiss`),
  /** 小爱 writes a colleague's next message to this customer; nothing is sent. */
  /** 认领: 手机打开 marks the customer as yours for half an hour, and the audit
   *  log keeps what for. also_by names whoever had opened them before you. */
  claim: (waID: string, why: string) =>
    post<{ claimed_by: string; claimed_at: string; also_by?: string; also_at?: string }>(`${lead(waID)}/claim`, { for: why }),
  /** The order's internal note, kept in the shop; "" clears it. */
  setOrderNote: (orderNo: string, note: string) =>
    put<{ note: string }>(`/console/api/orders/${encodeURIComponent(orderNo)}/note`, { note }),
  /** Every recent shop order under a customer's number, newest first; a null
   *  body when the copy tagged `tag` is still the answer. */
  orders: (tag = "", quiet = false) => getIfChanged<OrdersResponse>("/console/api/orders", tag, quiet),
  /** 价目表: both shops' prices, worked out on the server in the background;
   *  a null body when the copy tagged `tag` is still the answer. */
  prices: (tag = "") => getIfChanged<PriceSheet>("/console/api/prices", tag),
  /** Singapore's whole list, as the 价目表's editor saves it. */
  savePricesSG: (sg: PriceSG) => put<{ sg: PriceSG }>("/console/api/prices/sg", sg),
  /** 对话's 筛选: how many chats each product and each ad would list beside the rest of f, the list's own query
   *  (merged=1 as the list asks it); quiet, numbers beside choices. */
  leadFacets: (f: LeadFilters) =>
    get<LeadFacets>(`/console/api/leads/facets?${isWorkList(f) ? "" : "merged=1&"}${leadParams(f)}`, true),
  /** How many people are on each work list, by the lists' own rules, in one
   *  network ("" = both). */
  leadCounts: (network = "", quiet = false) =>
    get<Record<string, number>>(`/console/api/leads/counts${network ? `?network=${encodeURIComponent(network)}` : ""}`, quiet),
  /** A chat; quiet when read ahead of a click (the pointer resting on its row), so the bar along the top stays dark. */
  lead: (waID: string, quiet = false) => get<LeadDetail>(lead(waID), quiet),
  /** Whether anything in 对话 changed, and in the chat open ("" for none): asked every few seconds, so always quiet. */
  leadsPulse: (open: string) =>
    get<LeadPulse>(`/console/api/leads/pulse${open ? `?open=${encodeURIComponent(open)}` : ""}`, true),
  /** The customer's 广告足迹: how they first came, every ad tapped, every order; and each ad's picture. */
  leadRoad: (waID: string) => get<{ road: AdPerson; pics?: Record<string, string> }>(`${lead(waID)}/road`),
  /** The shop's orders placed under this conversation's number, read live from the shop. */
  leadOrders: (waID: string) => get<{ orders: ShopOrder[] }>(`${lead(waID)}/orders`),
  /** 小爱's kept reads of this customer, newest first (api console/workflow_aftersale.go aiReads). */
  leadAiReads: (waID: string) => get<{ reads: AIReadRow[] }>(`${lead(waID)}/ai-reads`),
  leadPhones: (waID: string) => get<{ phones: AttachedPhone[] }>(`${lead(waID)}/phones`),
  attachPhone: (waID: string, phone: string) => post<{ phone: string }>(`${lead(waID)}/phones`, { phone }),
  detachPhone: (waID: string, phone: string) => del<ActionResult>(`${lead(waID)}/phones/${encodeURIComponent(phone)}`),
  unlinkedOrders: () => get<{ orders: UnlinkedOrder[]; no_chat?: number }>("/console/api/orders/unlinked"),
  /** 「没有对话」: the shop's own checkout, the phone, a channel not wired here. */
  setOrderNoChat: (orderNo: string, on: boolean) =>
    post<{ order_no: string; no_chat: boolean }>(`/console/api/orders/${encodeURIComponent(orderNo)}/no-chat`, { no_chat: on }),
  setBot: (waID: string, enabled: boolean) => post<ActionResult>(`${lead(waID)}/bot`, { enabled }),
  /** Mark (or unmark) one of our own numbers. */
  setInternal: (waID: string, internal: boolean) =>
    post<{ internal: boolean }>(`${lead(waID)}/internal`, { internal }),
  /** Move the funnel stage by hand. The server locks the row afterwards: the
   *  rules may not take a colleague's decision back. */
  // Why they have not bought: one of LOST_REASONS; "" hands it back to 小爱's reading.
  setLeadReason: (waID: string, code: string) => post<{ reason: LeadReason | null }>(`${lead(waID)}/reason`, { code }),
  setLeadStage: (waID: string, stage: string) =>
    post<{ lead_stage: string; changed: boolean }>(`${lead(waID)}/stage`, { stage }),
  /** Which engine answers this one conversation; "default" follows the global setting. */
  setEngine: (waID: string, engine: "default" | "ai" | "playbook") =>
    post<{ engine: string }>(`${lead(waID)}/engine`, { engine }),
  setProduct: (waID: string, product: string) =>
    post<{ product: string }>(`/console/api/leads/${waID}/product`, { product }),
  setStatus: (waID: string, status: string) => post<ActionResult>(`${lead(waID)}/status`, { status }),
  setLabels: (waID: string, labels: string[]) => post<ActionResult>(`${lead(waID)}/labels`, { labels }),
  setReengage: (waID: string, optOut: boolean) =>
    post<ActionResult>(`${lead(waID)}/reengage`, { opt_out: optOut }),
  /**
   * Speak a typed reply in the agent's cloned voice and send it. This one
   * REALLY sends — unlike the older agent-message route, which only logs.
   */
  sendVoice: (waID: string, text: string) => post<ActionResult>(`${lead(waID)}/voice`, { text }),
  /**
   * Send a picture or an MP4 clip, with an optional caption. Multipart, not
   * JSON — the JSON helper caps bodies at 16 KB precisely so nobody sends a
   * file that way.
   */
  async sendMedia(waID: string, file: File, caption: string, replyTo = ""): Promise<ActionResult & { media_key?: string }> {
    const form = new FormData();
    form.set("caption", caption);
    form.set("file", file);
    if (replyTo) form.set("reply_to_id", replyTo);
    let res: Response;
    try {
      res = await apiFetch(`${BASE}${lead(waID)}/media`, {
        method: "POST",
        credentials: "omit",
        headers: multipartHeaders(),
        body: form,
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as ActionResult & { media_key?: string };
  },

  /**
   * Send audio the agent recorded themselves. Separate from sendVoice, which
   * speaks typed words in the cloned voice: the two are different acts and the
   * console offers them as two controls.
   */
  async sendRecording(waID: string, blob: Blob, filename: string): Promise<ActionResult> {
    const form = new FormData();
    form.set("file", new File([blob], filename, { type: blob.type }));
    let res: Response;
    try {
      res = await apiFetch(`${BASE}${lead(waID)}/recording`, {
        method: "POST",
        credentials: "omit",
        headers: multipartHeaders(),
        body: form,
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as ActionResult;
  },

  /**
   * Render the audio WITHOUT sending it, so the agent can hear it first. Returns
   * the ogg bytes, so it bypasses the JSON helper — but maps failures the same
   * way, because the block reasons are the whole point of the message.
   */
  async previewVoice(text: string): Promise<Blob> {
    let res: Response;
    try {
      res = await apiFetch(`${BASE}/console/api/voice/preview`, {
        method: "POST",
        credentials: "omit",
        headers: headers({ "Content-Type": "application/json" }),
        body: JSON.stringify({ text }),
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (res.status === 403) throw new ApiError(403, "You don't have access to that.");
    if (!res.ok) throw await errorFrom(res);
    return res.blob();
  },
  /**
   * Fetch a stored attachment as a blob.
   *
   * It cannot be an <img src> pointing at the API: this dashboard authenticates
   * with a bearer token, and an image request carries no headers. Fetching it
   * here keeps the object private — the alternative, a pre-signed URL, is a link
   * that opens the customer's photograph for anyone who has the string, which is
   * the property we just spent a migration getting rid of.
   */
  /** A stored file; signal drops it when nothing on screen wants it any more (Transcript's take). */
  media: (key: string, signal?: AbortSignal): Promise<Blob> => playableAt(`/console/api/media?key=${encodeURIComponent(key)}`, key, signal),

  /** Send a written reply. The customer has it the moment this resolves. */
  /** replyTo quotes one line of the chat, by its ref, as WhatsApp's 回复 does. */
  sendText: (waID: string, text: string, replyTo = "") =>
    post<{ ok: boolean; bot_enabled: boolean }>(`${lead(waID)}/text`, replyTo ? { text, reply_to_id: replyTo } : { text }),

  /** Send this conversation's refused uploads back to the ad platform. */
  retryConversions: (waID: string) => post<{ requeued: number }>(`${lead(waID)}/conversions/retry`),
  recordSale: (waID: string, value: number, currency: string, orderID: string) =>
    post<ActionResult>(`${lead(waID)}/sale`, { value, currency, order_id: orderID }),

  /**
   * CSV export needs the raw Response (blob body + the truncation header), so
   * it bypasses the JSON helper — but must map failures the same way.
   */
  async exportCsv(f: LeadFilters): Promise<{ blob: Blob; truncated: string | null }> {
    let res: Response;
    try {
      res = await fetch(`${BASE}/console/api/leads.csv?${leadParams(f)}`, {
        credentials: "omit",
        headers: headers(),
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (res.status === 403) throw new ApiError(403, "You don't have access to that.");
    if (!res.ok) throw await errorFrom(res);
    return { blob: await res.blob(), truncated: res.headers.get("X-Export-Truncated") };
  },

  /**
   * The 不合格 numbers, phone column only, for Meta's EXCLUSION audience.
   *
   * Deliberately not the leads CSV with a filter on it: that file carries names
   * and the last thing each customer said, and none of that has any business
   * being uploaded to an ad platform.
   */
  async exportDisqualified(): Promise<Blob> {
    let res: Response;
    try {
      res = await fetch(`${BASE}/console/api/leads/disqualified.csv`, {
        credentials: "omit",
        headers: headers(),
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (res.status === 403) throw new ApiError(403, "You don't have access to that.");
    if (!res.ok) throw await errorFrom(res);
    return res.blob();
  },

  // --- reporting ----------------------------------------------------------
  reengageStats: () => get<ReengageStats>("/console/api/reengage/stats"),
  overview: (range: string) => get<Overview>(`/console/api/report/overview?${range}`),
  campaigns: (range: string) => get<CampaignsResponse>(`/console/api/report/campaigns?${range}`),
  /** People grouped by the ad they tapped to reach us. */
  ads: (range: string, quiet = false) => get<AdsResponse>(`/console/api/report/ads?${range}`, quiet),
  /** 按来源: the ad's people counted apart from everybody else's. */
  origins: (range: string) =>
    get<{ origins: OriginReportRow[]; unplaced?: UnplacedSales }>(`/console/api/report/origins?${range}`),
  /** 花费: 小爱, WhatsApp and the voice notes, for the report's days. */
  costs: (range: string) => get<CostReport>(`/console/api/report/costs?${range}`),
  /** 增长: the money, the road from ad to cash, the biggest problem and today's cards. */
  growth: (query: string) => get<GrowthResponse>(`/console/api/report/growth?${query}`),
  /** 回传成绩 over the growth page's range. */
  capiScore: (query: string) => get<CapiScore>(`/console/api/report/capi?${query}`),
  /** 增长's cards: who took which, which are done, which were turned down. */
  growthActions: () => get<{ states: GrowthActionState[] }>("/console/api/growth/actions"),
  growthExperiments: () => get<{ experiments: GrowthExperiment[] }>("/console/api/growth/experiments"),
  createExperiment: (body: {
    card_key: string;
    product: string;
    hypothesis: string;
    metric: ExperimentMetric;
    control: ExperimentArm;
    trial: ExperimentArm;
    starts: string;
    ends?: string;
    min_people?: number;
  }) => post<GrowthExperiment>("/console/api/growth/experiments", body),
  closeExperiment: (id: number) => post<GrowthExperiment>(`/console/api/growth/experiments/${id}`, { close: true }),
  growthReviews: (limit = 8) => get<{ reviews: WeeklyReview[]; costs_hidden: boolean }>(`/console/api/growth/reviews?limit=${limit}`),
  setGrowthAction: (key: string, body: Partial<Omit<GrowthActionState, "key" | "updated_by" | "updated_at">>) =>
    post<GrowthActionState>(`/console/api/growth/actions/${encodeURIComponent(key)}`, body),
  /** Who is behind an ads row's ＋老客 / 其中老客. */
  adPeople: (range: string, ads: string[]) =>
    get<{ people: AdPerson[]; pics?: Record<string, string> }>(`/console/api/report/ads/people?${range}${range ? "&" : ""}ads=${encodeURIComponent(ads.join(","))}`),
  /** 客群: per product who asked and never bought, who bought, who is due. */
  segments: () => get<SegmentReport>("/console/api/report/segments"),
  /** One 客群 list's numbers, a phone column only, for a Meta customer list. */
  exportSegment: (product: SegmentProduct | "", kind: SegmentKind) =>
    blobAt(`/console/api/report/segments/export?product=${product}&kind=${kind}`),
  /** 客群 kept up to date in Meta every day: on or off (on runs it at once). */
  segmentAuto: (on: boolean) => post<SegmentSync>("/console/api/report/segments/auto", { on }),
  /** The daily update, now (after the token was fixed). */
  segmentSyncNow: () => post<{ started: boolean }>("/console/api/report/segments/sync"),
  /** The meeting-room screen's one request: today, as it happens. */
  /** The day's figures; another day with day (YYYY-MM-DD), as 今天 reads yesterday's hours. */
  live: (day = "", quiet = false) => get<LiveReport>(`/console/api/report/live${day ? `?day=${day}` : ""}`, quiet),
  /** A run of days, both ends included (the board's screens each set to their own days): the same answer, by the day too. */
  liveSpan: (from: string, to: string, quiet = false) => get<LiveReport>(`/console/api/report/live?from=${from}&to=${to}`, quiet),
  setSpend: (campaign: string, spend: number, currency: string) =>
    post<ActionResult>("/console/api/report/spend", { campaign, spend, currency }),
  /** since: a day before the regular thirty, read again from Meta (a gap in its counts). */
  refreshSpend: (since?: string) =>
    post<SpendRefreshResult>(`/console/api/report/spend/refresh${since ? `?since=${encodeURIComponent(since)}` : ""}`),
  /** Why an ad was switched off: every remark, newest first. */
  adRemarks: () => get<{ remarks: AdRemark[] }>("/console/api/ad-remarks"),
  addAdRemark: (body: { level: AdRemark["level"]; target: string; target_name: string; body: string }) =>
    post<AdRemark>("/console/api/ad-remarks", body),
  editAdRemark: (id: number, body: string) => post<AdRemark>(`/console/api/ad-remarks/${id}`, { body }),
  deleteAdRemark: (id: number) => del<{ deleted: number }>(`/console/api/ad-remarks/${id}`),

  // --- users / audit ------------------------------------------------------
  users: () => get<UsersResponse>("/console/api/users"),
  /**
   * Add a console account. The Google address IS the credential: whoever signs
   * in with it gets this account, so there is nothing to issue and nothing to
   * deliver to the person.
   */
  addUser: (email: string, name: string, role: string) =>
    post<ActionResult>("/console/api/users", { email, name, role }),
  /**
   * Change what a colleague may do. It applies to their live session on their
   * very next request — the API re-reads the user each time — so a demotion does
   * not wait for them to sign out.
   */
  setUserRole: (id: number, role: string) =>
    post<ActionResult>(`/console/api/users/${encodeURIComponent(String(id))}/role`, { role }),
  setUserActive: (id: number, active: boolean) =>
    post<ActionResult>(`/console/api/users/${encodeURIComponent(String(id))}/active`, { active }),
  /**
   * Point a console account at a Google address, releasing whatever account was
   * claimed on it. Covers both a changed address and a same-address Google
   * account that was recreated, because they are one operation.
   *
   * Sessions held by the previous account are revoked — unless you are editing
   * yourself, where you are already authenticated and a typo would lock you out.
   */
  setGoogleAccount: (id: number, email: string) =>
    post<ActionResult>(`/console/api/users/${encodeURIComponent(String(id))}/google`, { email }),
  /** What the team calls them. The server trims it and refuses an empty one. */
  renameUser: (id: number, name: string) =>
    post<{ id: number; name: string }>(`/console/api/users/${encodeURIComponent(String(id))}/name`, { name }),
  /**
   * Take a colleague off the team. The row is kept — their id is what the
   * playbook, 小爱's versions and the log point at — so this signs them out,
   * stops them signing in and drops them from the list. Adding the same
   * address again brings the row back.
   */
  removeUser: (id: number) => del<ActionResult>(`/console/api/users/${encodeURIComponent(String(id))}`),
  audit: (limit = 100) => get<AuditResponse>(`/console/api/audit?limit=${limit}`),
  /** 团队 › 动态: limit, and on a newer API before (a cursor), who and kind. Either shape; ActivityLog reads both. */
  auditLog: (query: string) => get<unknown>(`/console/api/audit?${query}`),

  // --- roles ----------------------------------------------------------------
  roles: () => get<RolesResponse>("/console/api/roles"),
  saveRole: (name: string, perms: string[]) => post<{ name: string }>("/console/api/roles", { name, perms }),
  deleteRole: (name: string) =>
    del<{ name: string }>(`/console/api/roles/${encodeURIComponent(name)}`),

  // --- operator settings ----------------------------------------------------
  /* --- the scripted conversation --------------------------------------- */

  playbook: (product?: string) =>
    get<PlaybookOverview>(`/console/api/playbook${product ? `?product=${encodeURIComponent(product)}` : ""}`),
  playbookProducts: () => get<{ products: PlaybookProduct[] }>("/console/api/playbook/products"),
  playbookSaveProduct: (p: { key: string; label: string; words: string; ord: number; hidden?: boolean }) =>
    post<{ product: PlaybookProduct }>("/console/api/playbook/products", p),
  playbookDeleteProduct: (key: string) =>
    del<ActionResult>(`/console/api/playbook/products/${encodeURIComponent(key)}`),
  playbookDiscard: (id: number) => post<ActionResult>(`/console/api/playbook/${id}/discard`),
  playbookVersion: (id: number) => get<PlaybookDetail>(`/console/api/playbook/${id}`),
  playbookStats: (id: number, days = 30) => get<PlaybookStats>(`/console/api/playbook/${id}/stats?days=${days}`),
  playbookDraft: (label: string, from: number, product?: string) =>
    post<{ id: number }>("/console/api/playbook/draft", { label, from, product }),
  playbookSaveStep: (id: number, step: PlaybookStep) =>
    post<ActionResult>(`/console/api/playbook/${id}/steps`, step),
  playbookDeleteStep: (id: number, key: string) =>
    del<ActionResult>(`/console/api/playbook/${id}/steps/${encodeURIComponent(key)}`),
  playbookPublish: (id: number) => post<ActionResult>(`/console/api/playbook/${id}/publish`),
  playbookReorder: (id: number, keys: string[]) =>
    post<ActionResult>(`/console/api/playbook/${id}/reorder`, { keys }),
  playbookDeleteAsset: (id: number, key: string) =>
    del<ActionResult>(`/console/api/playbook/${id}/assets/${encodeURIComponent(key)}`),
  /**
   * Upload a picture or clip. Multipart, not JSON — the JSON helper caps bodies
   * at 16 KB precisely so nobody sends a file that way.
   */
  async playbookUploadAsset(
    id: number,
    key: string,
    file: File,
    caption: string,
  ): Promise<{ ok: boolean; asset: PlaybookAsset }> {
    const form = new FormData();
    form.set("key", key);
    form.set("caption", caption);
    form.set("file", file);
    let res: Response;
    try {
      res = await apiFetch(`${BASE}/console/api/playbook/${id}/assets`, {
        method: "POST",
        credentials: "omit",
        headers: multipartHeaders(),
        body: form,
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as { ok: boolean; asset: PlaybookAsset };
  },
  /**
   * Walk one turn of a version, including an unpublished one.
   *
   * Stateless: the caller says which step the imaginary customer is standing on.
   * Server-side session state would have to be created, keyed and cleaned up,
   * and would make two people previewing the same draft interfere.
   */
  playbookPreview: (id: number, at: string, input: { text?: string; payload?: string }) =>
    post<PreviewTurn>(`/console/api/playbook/${id}/preview`, { at, ...input }),
  /**
   * A script picture, as a blob for the same reason as a transcript attachment:
   * an <img src> at the API carries no bearer token, and a pre-signed URL would
   * be a link that works for anyone holding it.
   */
  async playbookAsset(id: number, key: string): Promise<Blob> {
    let res: Response;
    try {
      res = await fetch(`${BASE}/console/api/playbook/${id}/asset/${encodeURIComponent(key)}`, {
        credentials: "omit",
        headers: headers(),
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (!res.ok) throw await errorFrom(res);
    return res.blob();
  },

  /* --- the AI's persona, knowledge and files ------------------------------ */

  ai: () => get<AIOverview>("/console/api/ai"),
  aiProfile: (id: number) => get<AIProfile>(`/console/api/ai/${id}`),
  /** A working copy. `from` names the version to copy — 0 = whatever is live,
   *  any other id = restoring that version, texts and files together. */
  aiDraft: (label: string, from: number) => post<{ id: number }>("/console/api/ai/draft", { label, from }),
  /**
   * The whole form at once. Not the small JSON helper: the knowledge text is
   * the product catalogue and the server caps this body at 4 MB, not 16 KB.
   */
  aiSave: (id: number, body: { label: string; persona: string; knowledge: string }) =>
    post<ActionResult>(`/console/api/ai/${id}`, body),
  /** The draft's 销售工作流 alone; the texts are not touched. */
  aiSaveWorkflow: (id: number, workflow: Workflow) =>
    post<{ ok: boolean; workflow: Workflow }>(`/console/api/ai/${id}/workflow`, { workflow }),
  /** Makes a version live. No exam is asked about since 2026-11-03 (owner: 「考试费太贵了」). */
  aiPublish: (id: number) => post<ActionResult>(`/console/api/ai/${id}/publish`),
  /** 回到上一版: a version that was live, live again at once, with no exam. */
  aiRollback: (id: number) => post<ActionResult & { published: number }>(`/console/api/ai/${id}/rollback`),
  /** 不能说的字: her real messages since yesterday read for them, by the program alone (free). */
  aiNeverSaid: () => get<AINeverSaid>("/console/api/ai/never-said", true),
  /** Save the list, between 「、」 or lines; "" puts the default back. */
  aiSetNeverWords: (words: string) => post<{ words: string[]; default: boolean }>("/console/api/ai/never-words", { words }),
  /** 用草稿重跑: one of her real turns put to the draft (profile_id) and to what is live; nothing reaches the customer.
   *  similar asks for the other customers' turns at the same step too, to put one at a time. About RM0.08 a press. */
  aiReplayChat: (body: { wa_id: string; at: string; profile_id: number; similar?: boolean }) =>
    post<AIReplayChat>("/console/api/ai/replay-chat", body),
  /* the exam: questions real customers asked, sat before publishing */
  aiExamCases: () => get<{ cases: AIExamCase[] }>("/console/api/ai-exam/cases"),
  aiAddExamCase: (body: ExamCaseBody) => post<{ id: number }>("/console/api/ai-exam/cases", body),
  aiUpdateExamCase: (id: number, body: ExamCaseBody) => post<ActionResult>(`/console/api/ai-exam/cases/${id}`, body),
  aiDeleteExamCase: (id: number) => del<ActionResult>(`/console/api/ai-exam/cases/${id}`),
  /** What a press would ask and about what it costs, before anything is spent (api console/ai_exam.go aiExamQuote). */
  aiExamQuote: (id: number, ask: ExamAsk) => get<AIExamQuote>(`/console/api/ai/${id}/exam/quote${examQuery(ask)}`),
  /** Starts a sitting on the number of questions its quote showed, and answers at once; poll aiExam until it is
   *  done. The server begins nothing without that number (owner, 2026-10-14: 「按了会扣钱」). */
  aiExamStart: (id: number, ask: ExamAsk, confirm: number) =>
    post<{ run_id: number }>(`/console/api/ai/${id}/exam${examQuery(ask, confirm)}`, {}),
  aiExam: (id: number) => get<AIExamState>(`/console/api/ai/${id}/exam`),
  /** How 小爱's turns sounded, a sample read each day: by day, by step and way, and the ones that did not sound like a colleague. */
  aiTone: (days = 7) => get<AIToneReport>(`/console/api/ai-tone?days=${days}`),
  /** Who changed which part of a version, each change compared line by line. */
  aiEdits: (id: number) => get<{ edits: AIEdit[]; authors: Record<string, string> }>(`/console/api/ai/${id}/edits`),
  /** Stops the running sitting: what it answered stays, nothing more is asked. */
  aiExamStop: (id: number) => post<{ stopped: number }>(`/console/api/ai/${id}/exam/stop`, {}),
  /** Each step of the live workflow with a B: how its two ways have done since the trial began (api console/workflow_trial.go). */
  workflowTrial: () => get<{ rows: WorkflowTrialRow[] }>("/console/api/workflow/trial"),
  /** The day's follow-up round and the last week's posted contents (api console/followup_board.go). */
  followupBoard: (quiet = false) => get<{ round: FollowupRound; contents: FollowupContent[] }>(FOLLOWUP_BOARD, quiet),
  /** A day's phone label changes; today's (Malaysian) without one. */
  followupLabels: (day?: string) => get<FollowupLabels>(`/console/api/followup/labels${day ? `?day=${day}` : ""}`),
  /** Post today's content for a group: a picture or clip with words under it, or words alone. */
  async followupPost(group: string, caption: string, file: File | null): Promise<{ ok: boolean; content: FollowupContent }> {
    const form = new FormData();
    form.set("group", group);
    form.set("caption", caption);
    if (file) form.set("file", file);
    let res: Response;
    try {
      res = await apiFetch(`${BASE}/console/api/followup/content`, {
        method: "POST",
        credentials: "omit",
        headers: multipartHeaders(),
        body: form,
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as { ok: boolean; content: FollowupContent };
  },
  /** Take back a content nothing was sent from yet. */
  followupDelete: (id: number) => del<{ ok: boolean }>(`/console/api/followup/content/${id}`),
  /** Send a content of today's to its group's customers still inside WhatsApp's 24 hours. */
  followupSend: (id: number, owner = "") => post<FollowupSendResult>(`/console/api/followup/content/${id}/send`, { owner }),
  /** Who can be picked as 负责 and who has which customer (api console/owner.go). */
  owners: () => get<{ names: string[]; owners: Record<string, string> }>("/console/api/owners"),
  /** Give customers to a colleague; "" = nobody's. */
  setOwners: (waIDs: string[], owner: string) => post<{ updated: number; owner: string }>("/console/api/owners", { wa_ids: waIDs, owner }),
  /** The live workflow's steps after a sale, by product, for the customer table's 回访. */
  workflowAftersale: () => get<{ products: Record<string, Record<string, AftersaleStep>> }>("/console/api/workflow/aftersale"),
  /** One file a live check-in sends, to save on the phone. */
  workflowAftersaleFile: (key: string) => blobAt(`/console/api/workflow/aftersale/file/${encodeURIComponent(key)}`),
  /** A picture a colleague sent, copied into a working copy's files. */
  aiAssetFromMedia: (id: number, body: { media_key: string; label: string; when_to_send: string; valid_until: string }) =>
    post<{ ok: boolean; asset: AIAssetRow }>(`/console/api/ai/${id}/assets/from-media`, body),
  /* the weekly report */
  /** `query` is the report page's own range (from/to, or all=1), so the
   *  waiting numbers count the same days as the tiles above them. */
  insights: (query: string) => get<InsightState>(`/console/api/report/insights${query ? `?${query}` : ""}`),
  runInsights: (days: number) => post<{ id: number }>("/console/api/report/insights", { days }),
  aiDiscard: (id: number) => post<ActionResult>(`/console/api/ai/${id}/discard`),
  aiDescribeAsset: (id: number, key: string, body: { label: string; when_to_send: string; valid_until: string }) =>
    post<ActionResult>(`/console/api/ai/${id}/assets/${encodeURIComponent(key)}`, body),
  aiDeleteAsset: (id: number, key: string) =>
    del<ActionResult>(`/console/api/ai/${id}/assets/${encodeURIComponent(key)}`),
  /** 同事成交片段, and 「不要学这段」. */
  aiMoments: () => get<MomentsState>("/console/api/ai-moments"),
  /** 回放测试: one run whole (the newest without an id), the runs, what would be replayed now. */
  aiReplays: (id?: number) => get<ReplayState>(`/console/api/ai-replay${id ? `?id=${id}` : ""}`),
  /** Starts a replay of the colleagues' moments on a version; poll aiReplays until it is done.
   *  mode is the way she is asked, "" for the ai_prompt_mode setting. */
  aiReplayStart: (profileID: number, body: { provider: string; model: string; mode: PromptMode }) =>
    post<{ run_id: number }>(`/console/api/ai/${profileID}/replay`, body),
  aiSetMomentOff: (id: number, off: boolean) => post<{ id: number; off: boolean }>(`/console/api/ai-moments/${id}`, { off }),
  /** The switch at the top of the AI page: who answers customers. */
  /** The draft's undo stack: what it held before each save, newest first. */
  aiHistory: (id: number) => get<{ history: AISnapshot[]; authors: Record<string, string> }>(`/console/api/ai/${id}/history`),
  /** Put one back. It is an ordinary save, so it is itself undoable. */
  aiRestoreSnapshot: (id: number, snap: number) =>
    post<ActionResult>(`/console/api/ai/${id}/history/${snap}`, {}),
  /** Put one named situation in force — writes every setting it implies. */
  aiSetMode: (mode: string) => post<{ mode: string }>("/console/api/ai/mode", { mode }),
  aiSetEngine: (engine: string) => post<ActionResult>("/console/api/ai/engine", { engine }),
  /** Put every conversation pinned to an engine back on 「跟随默认」. */
  aiUnpin: () => post<{ moved: number }>("/console/api/ai/unpin", {}),
  /** What the script does with a line it cannot match: "ai" | "handoff". */
  aiSetFallback: (fallback: string) => post<ActionResult>("/console/api/ai/fallback", { fallback }),
  /** Messenger's own 谁在回: "ai" | "playbook" | "human", or "" to follow the switch. */
  aiSetMessenger: (engine: string) => post<{ messenger: string }>("/console/api/ai/messenger", { engine }),
  /** The night hours 小爱 answers WhatsApp in; "" switches them off. */
  aiSetNight: (hours: string) => post<{ night_hours: string }>("/console/api/ai/night", { hours }),
  /** 小爱's follow-up: "on" | "test" | "" (nobody). */
  aiSetFollowUp: (mode: string) => post<{ followup: string }>("/console/api/ai/followup", { mode }),
  aiSetVoice: (mode: string) => post<{ voice: string }>("/console/api/ai/voice", { mode }),
  /** Where 小爱 reads the customer first: "" grain only, "all", "off". */
  aiSetRead: (mode: string) => post<{ read: string }>("/console/api/ai/read", { mode }),
  /** The 广告价: a cp campaign sold only on its own page, or "" for none. */
  aiSetAdPrice: (campaign: string) => post<AIAdPrice>("/console/api/ai/ad-price", { campaign }),
  /** 花费: what the AI cost over the last days, or the days picked (from, to: yyyy-mm-dd), at list price. */
  aiUsage: (days: number | { from: string; to: string }) =>
    get<AIUsage>(`/console/api/ai/usage?${typeof days === "number" ? `days=${days}` : `from=${days.from}&to=${days.to}`}`),
  /** 连线: the last hour's replies and on which model (no money in it). */
  aiHealth: () => get<AIHealth>("/console/api/ai/health"),
  /** 小爱 vs 同事: six weeks of new customers by who answered first. */
  /** 接待数据 for the days picked, both included: "2026-09-11". */
  aiVersus: (from: string, to: string) =>
    get<AIVersus>(`/console/api/ai/versus?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  /** 话术榜 for the days picked (both included), whose chats, which product ("" for all). */
  board: (from: string, to: string, who: "cs" | "ai", product: string) =>
    get<BoardPage>(
      `/console/api/board?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&who=${who}&product=${encodeURIComponent(product)}`,
    ),
  /** Hand one trial number's conversation back to her. Only numbers already
   *  on the trial list, and only ON: silencing a conversation is a decision
   *  about a customer mid-sentence and stays on the conversation page. */
  aiResumeTester: (number: string) =>
    post<{ number: string; bot_enabled: boolean }>("/console/api/ai/testers/bot", { number }),
  /** Who 小爱 answers while the script is still the default for the rest. */
  aiSetTesters: (testers: string) => post<ActionResult>("/console/api/ai/testers", { testers }),
  aiTry: (body: {
    profile_id: number;
    text: string;
    history: AITrialTurn[];
    provider?: string;
    model?: string;
    /** A picture the pretend customer attached, as a data URL. */
    image?: string;
    /** "full" or "split" in place of the ai_prompt_mode setting; "" or absent follows it. */
    mode?: PromptMode;
  }) =>
    post<AITrial>("/console/api/ai/try", body),
  /** Upload a picture, clip or voice note. Multipart, like the playbook's. */
  async aiUploadAsset(
    id: number,
    key: string,
    file: File,
    label: string,
    whenToSend: string,
    validUntil = "",
  ): Promise<{ ok: boolean; asset: AIAsset }> {
    const form = new FormData();
    form.set("key", key);
    form.set("label", label);
    form.set("when_to_send", whenToSend);
    form.set("valid_until", validUntil);
    form.set("file", file);
    let res: Response;
    try {
      res = await apiFetch(`${BASE}/console/api/ai/${id}/assets`, {
        method: "POST",
        credentials: "omit",
        headers: multipartHeaders(),
        body: form,
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as { ok: boolean; asset: AIAsset };
  },
  /**
   * 素材库: a file added — or, with `replace`, put in place of one she has,
   * keeping its words unless new ones are given — live at once as a new
   * version. Text changes still go through a draft and the exam.
   */
  async libraryUpload(body: {
    file: File;
    label?: string;
    when_to_send?: string;
    valid_until?: string;
    replace?: string;
  }): Promise<{ ok: boolean; id: number; asset?: AIAsset }> {
    const form = new FormData();
    if (body.replace) form.set("replace", body.replace);
    if (body.label !== undefined) form.set("label", body.label);
    if (body.when_to_send !== undefined) form.set("when_to_send", body.when_to_send);
    if (body.valid_until !== undefined) form.set("valid_until", body.valid_until);
    form.set("file", body.file);
    let res: Response;
    try {
      res = await apiFetch(`${BASE}/console/api/library/assets`, {
        method: "POST",
        credentials: "omit",
        headers: multipartHeaders(),
        body: form,
      });
    } catch {
      throw networkError();
    }
    if (res.status === 401) {
      clearToken();
      onUnauthorized?.();
      throw new ApiError(401, "unauthorized");
    }
    if (!res.ok) throw await errorFrom(res);
    return (await res.json()) as { ok: boolean; id: number; asset?: AIAsset };
  },
  /** 素材库: a picture or clip a colleague sent a customer, added live. */
  libraryFromMedia: (body: { media_key: string; label: string; when_to_send: string; valid_until: string }) =>
    post<{ ok: boolean; id: number; asset?: AIAsset }>("/console/api/library/from-media", body),
  /** 素材库: a file's words and last day, changed live. */
  libraryDescribe: (key: string, body: { label: string; when_to_send: string; valid_until: string }) =>
    post<{ ok: boolean; id: number }>(`/console/api/library/assets/${encodeURIComponent(key)}`, body),
  /** 素材库: a file taken out, live. */
  libraryRemove: (key: string) => del<{ ok: boolean; id: number }>(`/console/api/library/assets/${encodeURIComponent(key)}`),
  /** 素材库: the clips colleagues sent customers lately. */
  libraryVideos: (days = 21) => get<{ days: number; videos: LibraryVideo[] }>(`/console/api/library/colleague-videos?days=${days}`),
  libraryVoices: (days = 30) => get<{ days: number; voices: LibraryVoice[] }>(`/console/api/library/colleague-voices?days=${days}`),
  /**
   * A profile's file, as a blob, for the same reason as a playbook picture —
   * a recording as this device can play it (playableAt).
   */
  aiAsset: (id: number, key: string): Promise<Blob> => playableAt(`/console/api/ai/${id}/asset/${encodeURIComponent(key)}`, key),
  /**
   * A file made small (?w=): a picture shrunk, or a clip's frame, as WebP —
   * what a page of a hundred cards shows (API: console/preview.go). The whole
   * file still comes from aiAsset and media, when somebody opens it. `v` names
   * the file behind the key, so the browser may keep the preview for good
   * and still sees 换一张.
   */
  aiAssetPreview: (id: number, key: string, objectKey: string, w: number) =>
    blobAt(`/console/api/ai/${id}/asset/${encodeURIComponent(key)}?w=${w}&v=${encodeURIComponent(objectKey)}`),
  /** A conversation's file made small; a key names one file for ever. */
  mediaPreview: (key: string, w: number) => blobAt(`/console/api/media?key=${encodeURIComponent(key)}&w=${w}`),

  settings: () => get<SettingsResponse>("/console/api/settings"),
  /** Saving "" is a real action: clearing the Meta test code ends a test. */
  setSetting: (key: string, value: string) =>
    post<{ key: string; value: string }>("/console/api/settings", { key, value }),
};
